import React from 'react'
import { Box, Typography, useMediaQuery, Icon, Card, Divider, Skeleton } from '@mui/material'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTheme } from '@mui/material/styles'
import { format, parseISO } from 'date-fns'

// Local Imports
import AdxStatusBar from 'components/reusable/statusBar/AdxStatusBar'
import leaveRequestStatusHandler from 'module/leave/util/leaveRequestStatusHandler'
import EmptyData from 'module/leave/pages/createRequest/components/emptyData'

// API services
import { useGetAllLeaveCreditRequest } from 'services/api/modules/leave/leaveRequest'
import AdxEmptyStateV2 from 'components/reusable/emptyState/AdxEmptyStateV2'

const LeaveCreditTable = () => {
  const theme = useTheme()
  const location = useLocation()
  const navigate = useNavigate()
  const matches = useMediaQuery(theme.breakpoints.up('lg'))

  const { data, isLoading } = useGetAllLeaveCreditRequest({
    type: 3,
    limit: 10,
    page: 1,
  })

  let skeletonArray = new Array(5).fill('i')

  return (
    <Card
      variant="outlined"
      sx={{
        borderRadius: '4.5px',
        backgroundColor: 'white.main',
        width: '100%',
        maxHeight: 'calc(100vh - 267px)',
        overflow: 'auto',
      }}
      className="scrollbar"
    >
      <Box p={2} display={'flex'} width={1} gap={1}>
        <Typography sx={{ width: { xs: 1, lg: 1 / 3 } }} variant="body2">
          Request
        </Typography>
        {matches ? (
          <Typography sx={{ width: 1 / 3 }} variant="body2">
            Reason
          </Typography>
        ) : null}
        <Typography sx={{ width: { xs: 1, lg: 1 / 3 } }} variant="body2">
          Status
        </Typography>
      </Box>
      <Divider variant="light" />

      {isLoading ? (
        skeletonArray.map((arr, index) =>
          matches ? (
            <React.Fragment key={index}>
              <Box display={'flex'}>
                <Box p={2} mr={6}>
                  <Skeleton height={30} width={200} />
                </Box>
                <Box p={2} mr={18}>
                  <Skeleton height={30} width={300} />
                </Box>
                <Box p={2}>
                  <Skeleton height={30} width={200} />
                </Box>
              </Box>
              <Divider light />
            </React.Fragment>
          ) : (
            <React.Fragment key={index}>
              <Box display={'flex'} flexDirection={'row'} width="100%">
                <Box p={2} width="50%">
                  <Skeleton height={50} width={'100%'} />
                </Box>
                <Box p={2} width="50%">
                  <Skeleton height={50} width={'100%'} />
                </Box>
              </Box>

              <Divider light />
            </React.Fragment>
          )
        )
      ) : data ? (
        data?.data?.map((creditRequest, index) => {
          return (
            <React.Fragment key={index}>
              <Box
                onClick={() =>
                  navigate(`/app/leave/leave_credit/${creditRequest.id}`, {
                    state: {
                      backgroundLocation: location,
                    },
                  })
                }
                sx={{ overflow: 'auto', cursor: 'pointer' }}
                p={2}
                display={'flex'}
                alignItems={'center'}
                gap={1}
              >
                <Box
                  sx={{ width: { xs: 1, lg: 1 / 3 } }}
                  display={'flex'}
                  flexDirection={{ xs: 'column', sm: 'row' }}
                  alignItems={{ xs: 'start', sm: 'center' }}
                  justifyContent={'start'}
                  gap={1}
                >
                  <Typography sx={{}} variant="subtitle1" color="alternate.main">
                    {creditRequest.duration} {creditRequest.duration === 1 ? 'day' : 'days'}
                  </Typography>

                  {matches ? (
                    <Icon
                      sx={{
                        fontSize: 7,
                        color: 'light-gray-text.main',
                      }}
                    >
                      circle
                    </Icon>
                  ) : null}
                  <Typography variant="subtitle1" color="light-gray-text.main">
                    {format(parseISO(creditRequest.start_date), `dd/MM/yyyy`)} -{' '}
                    {format(parseISO(creditRequest.end_date), `dd/MM/yyyy`)}
                  </Typography>
                </Box>
                {matches ? (
                  <Box width={1 / 3}>
                    <Typography noWrap variant="subtitle1" color="dark-gray-text.main">
                      {creditRequest.reason}
                    </Typography>
                  </Box>
                ) : null}

                <Box
                  display={'flex'}
                  gap={{ xs: 1, lg: 2 }}
                  justifyContent="space-between"
                  alignItems="center"
                  sx={{ width: { xs: 1, lg: 1 / 3 } }}
                >
                  <AdxStatusBar status={leaveRequestStatusHandler(creditRequest.status)} />
                  <Typography
                    color="alternate.main"
                    sx={{
                      padding: 1,
                      boxSizing: 'border-box',
                      cursor: 'pointer',
                      fontWeight: 'medium',
                    }}
                    variant="subtitle1"
                  >
                    View
                  </Typography>
                </Box>
              </Box>
              <Divider light />
            </React.Fragment>
          )
        })
      ) : (
        <AdxEmptyStateV2
          icon={<Icon>calendar_today</Icon>}
          title="No leave credit request found"
          subtitle="Click on the button above to create leave credit request"
        />
      )}
    </Card>
  )
}

export default LeaveCreditTable
